import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return [
      "Loader",
      "Container",
      "SelectedSessionContainer",
      "SelectedSessionName",
      "PortalLink",
      "VideoUrlLink",
      "PsVideoUrlLink",
      "VideoUrlSyncedAt",
      "CuepointsList",
      "CuepointsSyncedAt",
      "SelectFromSessionsContainer",
      "SessionsSelectInput",
      "YearInput",
      "ErrorMessage",
    ]
  }

  initialize() {
    this.sessionId = this.data.get("sessionId")
    this.storeSessionIdPath = this.data.get("storeSessionIdPath")
    this.syncPath = this.data.get("syncPath")
    this.sessionsProxyPath = this.data.get("sessionsProxyPath")
    this.sessionProxyPath = this.data.get("sessionProxyPath")
    this.sessionCuepointsProxyPath = this.data.get("sessionCuepointsProxyPath")
    this.portalUrl = this.data.get("portalUrl")
    this.session = {}
    this.sessionsList = []

    $(this.LoaderTarget).find(".loading-spinner-container").removeClass("hidden")

    if (this.sessionId !== "") {
      this.fetchSessionData()
    } else {
      this.fetchSessionsListData()
    }
  }

  showContainer() {
    $(this.LoaderTarget).find(".loading-spinner-container").addClass("hidden")
    this.LoaderTarget.classList.add("hidden")
    this.ContainerTarget.classList.remove("hidden")
  }

  showLoader() {
    $(this.LoaderTarget).find(".loading-spinner-container").removeClass("hidden")
    this.LoaderTarget.classList.remove("hidden")
    this.ContainerTarget.classList.add("hidden")
  }

  showSelectedSessionContainer() {
    this.SelectedSessionContainerTarget.classList.remove("hidden")
  }

  showSessionsList() {
    this.SelectFromSessionsContainerTarget.classList.remove("hidden")
  }

  // The proxy answers non-200s with { response_body }, e.g. a 502 when Open
  // Media is unreachable. Surface it instead of spinning forever.
  showApiError(message) {
    this.ErrorMessageTarget.innerText = message || "Open Media did not respond."
    this.ErrorMessageTarget.classList.remove("hidden")
    this.showContainer()
  }

  clearApiError() {
    this.ErrorMessageTarget.innerText = ""
    this.ErrorMessageTarget.classList.add("hidden")
  }

  handleSessionsListResponse(response) {
    if (!response || !Array.isArray(response.results)) {
      this.showApiError(response && response.response_body)
      return
    }
    if (response.truncated) {
      this.showApiError(`Open Media returned only ${response.results.length}${response.count ? ` of ${response.count}` : ""} sessions for this year. If yours is missing, try again in a minute.`)
    } else {
      this.clearApiError()
    }
    this.sessionsList = response.results
    this.setUiSessionsSelectInput()
    this.showSessionsList()
    this.showContainer()
  }

  hideSelectedSession() {
    this.SelectedSessionContainerTarget.classList.add("hidden")
  }

  hideSessionsList() {
    this.SelectFromSessionsContainerTarget.classList.add("hidden")
  }

  // v2 sessions carry no `url`; the public page is {portal}/sessions/{id}
  sessionPageUrl(sessionId) {
    return sessionId ? `${this.portalUrl}/sessions/${sessionId}` : this.portalUrl
  }

  // Titles and URLs below come from the external Open Media API. Text goes in
  // as text, and only http(s) URLs become links -- a javascript: URL would run
  // when a staff member clicked it.
  safeHref(url) {
    return /^https?:\/\//i.test(url || "") ? url : ""
  }

  setUiSessionTitle() {
    if (this.session.title) {
      this.SelectedSessionNameTarget.textContent = this.session.title
      this.PortalLinkTarget.href = this.sessionPageUrl(this.session.id)
    }
  }

  setUiSessionVideoUrl() {
    this.VideoUrlLinkTarget.textContent = this.session.video_url || ""
    this.VideoUrlLinkTarget.href = this.safeHref(this.session.video_url)
  }

  setUiSessionPsVideoUrl() {
    this.PsVideoUrlLinkTarget.textContent = this.session.video_url || ""
    this.PsVideoUrlLinkTarget.href = this.safeHref(this.session.video_url)
    this.VideoUrlSyncedAtTarget.innerHTML = strftime("%B %d, %Y, %l:%M %P", new Date())
  }

  setUiCuepointsList(cuepointsList) {
    $(this.CuepointsListTarget).find('li').remove()

    cuepointsList.map((cuepoint) => {
      var li = document.createElement('li')
      li.classList.add("cuepoint-list-item", "mt-2")
      var itemLink = document.createElement('a')
      itemLink.textContent = cuepoint.title
      itemLink.href = this.safeHref(cuepoint.url)
      itemLink.setAttribute('target', '_blank')
      li.appendChild(itemLink)
      var videoElem = document.createElement('span')
      videoElem.classList.add("cuepoint-item-video-url", "ml-2")
      videoElem.innerHTML = "&mdash;"
      var videoLink = document.createElement('a')
      videoLink.textContent = cuepoint.video_url
      videoLink.href = this.safeHref(cuepoint.video_url)
      videoLink.setAttribute('target', '_blank')
      videoElem.appendChild(videoLink)
      li.appendChild(videoElem)
      this.CuepointsListTarget.appendChild(li)
    })
  }

  setUiCuepointsUpdatedAt() {
    this.CuepointsSyncedAtTarget.innerHTML = strftime("%B %d, %Y, %l:%M %P", new Date())
  }

  setUiSessionsSelectInput() {
    Array.from(this.SessionsSelectInputTarget.options).map((_, index) => {
      this.SessionsSelectInputTarget.options[0] = null
    })

    this.sessionsList.map((session) => {
      var option = document.createElement("option")
      option.value = session.id
      option.text = session.title
      this.SessionsSelectInputTarget.add(option)
    })
  }

  fetchSessionData() {
    fetch(this.sessionProxyPath, {
      method: "GET",
      credentials: "same-origin",
      headers:  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(response => {
        if (!response || response.response_body) {
          this.showApiError(response && response.response_body)
          return
        }
        this.clearApiError()
        this.session = response
        this.setUiSessionTitle()
        this.setUiSessionVideoUrl()
        this.showContainer()
      })
      .catch((e) => { console.error(e); this.showApiError() })
  }

  fetchSessionsListData() {
    this.showLoader()
    this.hideSelectedSession()

    fetch(this.sessionsProxyPath, {
      method: "GET",
      credentials: "same-origin",
      headers:  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(response => this.handleSessionsListResponse(response))
      .catch((e) => { console.error(e); this.showApiError() })
  }

  onLoadByYear() {
    var year = $(this.YearInputTarget).val()

    this.showLoader()
    this.hideSelectedSession()

    fetch(`${this.sessionsProxyPath}?year=${year}`, {
      method: "GET",
      credentials: "same-origin",
      headers:  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(response => this.handleSessionsListResponse(response))
      .catch((e) => { console.error(e); this.showApiError() })
  }

  onSelectSession() {
    var selectedSessionId = $(this.SessionsSelectInputTarget).val()
    // v2 ids are integers; the <select> value is always a string
    var session = this.sessionsList.find((s) => String(s.id) === String(selectedSessionId))

    if (!session) {
      this.showApiError("No Open Media session selected. Refresh the session list and pick one.")
      return
    }

    this.clearApiError()
    this.session = session
    this.sessionId = String(session.id)
    this.setUiSessionTitle()
    this.setUiSessionVideoUrl()
    this.storeSessionIdOnMeeting()
    this.hideSessionsList()
    this.showSelectedSessionContainer()
    this.showContainer()
  }

  onClearSelectedSession() {
    this.session = { id: null, title: null }
    this.sessionId = ""
    this.SelectedSessionNameTarget.innerHTML = ""
    this.PortalLinkTarget.href = "javascript:void(0)"
    this.storeSessionIdOnMeeting()
    this.fetchSessionsListData()
  }

  onCancelSessionSelect() {
    this.hideSessionsList()
    this.showSelectedSessionContainer()
    this.showContainer()
  }

  storeSessionIdOnMeeting() {
    fetch(this.storeSessionIdPath, {
      method: "PUT",
      credentials: "same-origin",
      headers:  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ meeting: { open_media_session_id: this.session.id }})
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Saving the session on the meeting failed (HTTP ${response.status}). Reload the tab before syncing.`)
        }
        return response.json()
      })
      .then(() => this.clearApiError())
      .catch((e) => { console.error(e); this.showApiError(e.message) })
  }

  onSyncVideoUrls() {
    fetch(`${this.syncPath}?session_id=${this.sessionId}&attr=video_url`, {
      method: "GET",
      credentials: "same-origin",
      headers:  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(response => {
        if (!response || response.response_body) {
          this.showApiError(response && response.response_body)
          return
        }
        this.clearApiError()
        this.session = response
        this.setUiSessionTitle()
        this.setUiSessionVideoUrl()
        this.setUiSessionPsVideoUrl()
      })
      .catch((e) => { console.error(e); this.showApiError() })
  }

  onSyncCuepoints() {
    fetch(`${this.syncPath}?session_id=${this.sessionId}&attr=cuepoints`, {
      method: "GET",
      credentials: "same-origin",
      headers:  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(response => {
        if (!Array.isArray(response)) {
          this.showApiError(response && response.response_body)
          return
        }
        if (response.length === 0) {
          // The server links nothing when Open Media has no cue points on a
          // published video yet (and does not stamp synced_at), or when no cue
          // point title matched an agenda item. Leave the list and timestamp as
          // they are and say so; reload the tab to see the stored state.
          this.showApiError("No agenda items were linked. Either Open Media has no cue points on a published video for this session yet, or none of the cue point titles matched an agenda item. Reload the tab to see the stored state.")
          return
        }
        this.clearApiError()
        this.setUiCuepointsList(response)
        this.setUiCuepointsUpdatedAt()
      })
      .catch((e) => { console.error(e); this.showApiError() })
  }

}